import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CalendarDays, Plus, Clock, MapPin, Video, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";

const STATUS_CLS = {
  Scheduled: "rag-amber",
  Cleared: "rag-green",
  Selected: "rag-green",
  Rejected: "rag-red",
  Cancelled: "rag-red",
  Rescheduled: "rag-amber",
  Completed: "rag-green",
};

function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay(); // Sun=0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

function fmtDate(d) {
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function Schedule() {
  const [items, setItems] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ stage: "L1", duration_min: 30, location: "Zoom", status: "Scheduled" });

  const load = async () => {
    const { data } = await api.get("/interviews");
    setItems(data);
  };
  useEffect(() => {
    load();
    api.get("/candidates").then((r) => setCandidates(r.data)).catch(() => {});
  }, []);

  const days = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const byDay = useMemo(() => {
    const map = {};
    days.forEach((d) => { map[d.toDateString()] = []; });
    items.forEach((it) => {
      const d = new Date(it.scheduled_at);
      const key = d.toDateString();
      if (key in map) map[key].push(it);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)));
    return map;
  }, [items, days]);

  const upcoming = useMemo(() => {
    const now = new Date();
    return items
      .filter((i) => new Date(i.scheduled_at) >= now && ["Scheduled", "Rescheduled"].includes(i.status))
      .slice(0, 10);
  }, [items]);

  const shift = (weeks) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + weeks * 7);
    setWeekStart(d);
  };

  const openNew = (day) => {
    const base = day ? new Date(day) : new Date();
    base.setHours(11, 0, 0, 0);
    setForm({
      stage: "L1",
      duration_min: 30,
      location: "Zoom",
      status: "Scheduled",
      scheduled_at: new Date(base.getTime() - base.getTimezoneOffset() * 60000).toISOString().slice(0, 16),
    });
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (it) => {
    const iso = new Date(new Date(it.scheduled_at).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setEditing(it);
    setForm({ ...it, scheduled_at: iso });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (!form.candidate_id) return toast.error("Pick a candidate first");
      if (!form.scheduled_at) return toast.error("Choose a date and time");
      const payload = { ...form, scheduled_at: new Date(form.scheduled_at).toISOString() };
      if (editing?.id) {
        await api.patch(`/interviews/${editing.id}`, payload);
        toast.success("Interview updated");
      } else {
        await api.post("/interviews", payload);
        toast.success("Interview scheduled");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async () => {
    if (!editing?.id) return;
    if (!window.confirm("Cancel this interview?")) return;
    await api.delete(`/interviews/${editing.id}`);
    toast.success("Cancelled");
    setOpen(false);
    setEditing(null);
    load();
  };

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Interview Calendar</div>
          <h1 className="font-heading text-3xl font-bold text-[var(--yc-navy)] tracking-tight mt-1 flex items-center gap-3">
            <CalendarDays className="h-6 w-6 text-[var(--yc-emerald)]" /> Schedule
          </h1>
          <p className="text-slate-600 text-sm mt-1">Book L1 &amp; L2 interviews. Candidate stage dates sync automatically.</p>
        </div>
        <Button onClick={() => openNew()} className="rounded-full bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="new-interview-button">
          <Plus className="h-4 w-4 mr-1" /> Schedule interview
        </Button>
      </div>

      {/* Week nav */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="week-prev">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="font-heading font-semibold text-[var(--yc-navy)]">
          {days[0].toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} — {days[6].toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
        </div>
        <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="week-next">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(new Date()))}>
          Today
        </Button>
      </div>

      {/* Week grid */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {days.map((d) => {
          const isToday = d.toDateString() === new Date().toDateString();
          const list = byDay[d.toDateString()] || [];
          return (
            <button
              key={d.toDateString()}
              onClick={() => openNew(d)}
              className={`text-left bg-white border rounded-lg p-3 min-h-40 hover:border-[var(--yc-emerald)] transition-colors ${
                isToday ? "border-[var(--yc-emerald)] ring-1 ring-[var(--yc-emerald)]/30" : "border-slate-200"
              }`}
              data-testid={`day-${d.toISOString().slice(0,10)}`}
            >
              <div className="flex items-center justify-between">
                <div className="label-eyebrow">{d.toLocaleDateString("en-IN", { weekday: "short" })}</div>
                <div className={`font-heading font-bold text-xl ${isToday ? "text-[var(--yc-emerald)]" : "text-[var(--yc-navy)]"}`}>
                  {d.getDate()}
                </div>
              </div>
              <div className="mt-2 space-y-1.5">
                {list.length === 0 && <div className="text-[11px] text-slate-400">Tap to add</div>}
                {list.map((it) => (
                  <div
                    key={it.id}
                    onClick={(e) => { e.stopPropagation(); openEdit(it); }}
                    className="border-l-2 border-[var(--yc-gold)] bg-slate-50 hover:bg-slate-100 px-2 py-1.5 rounded-r-md cursor-pointer"
                    data-testid={`interview-${it.id}`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <div className="text-[11px] font-mono text-slate-500">{fmtTime(it.scheduled_at)}</div>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold ${STATUS_CLS[it.status] || "rag-amber"}`}>
                        {it.stage}
                      </span>
                    </div>
                    <div className="text-xs font-medium text-[var(--yc-navy)] truncate">{it.candidate_name}</div>
                    <div className="text-[10px] text-slate-500 truncate">{it.role_applied}</div>
                  </div>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {/* Upcoming list */}
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="upcoming-interviews">
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="label-eyebrow">Upcoming</div>
          <div className="font-heading font-semibold text-[var(--yc-navy)]">Next 10 interviews across all recruiters</div>
        </div>
        <div className="divide-y divide-slate-100">
          {upcoming.length === 0 && (
            <div className="px-6 py-8 text-center text-sm text-slate-500">Nothing scheduled. Book your first interview from the calendar above.</div>
          )}
          {upcoming.map((it) => (
            <div key={it.id} className="px-6 py-3 flex items-center gap-4 hover:bg-slate-50 cursor-pointer" onClick={() => openEdit(it)}>
              <div className="text-center min-w-16">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{new Date(it.scheduled_at).toLocaleDateString("en-IN", { month: "short" })}</div>
                <div className="font-heading text-2xl font-bold text-[var(--yc-navy)]">{new Date(it.scheduled_at).getDate()}</div>
                <div className="text-xs text-slate-500 font-mono">{fmtTime(it.scheduled_at)}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-medium text-[var(--yc-navy)]">{it.candidate_name}</div>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold ${STATUS_CLS[it.status] || "rag-amber"}`}>{it.stage}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5 flex gap-3">
                  <span>{it.role_applied} · {it.branch}</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {it.duration_min}m</span>
                  {it.location && <span className="flex items-center gap-1">{it.location === "Zoom" ? <Video className="h-3 w-3" /> : <MapPin className="h-3 w-3" />} {it.location}</span>}
                  {it.interviewer && <span>with {it.interviewer}</span>}
                </div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${STATUS_CLS[it.status] || "rag-amber"}`}>{it.status}</span>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">{editing?.id ? "Update interview" : "Schedule interview"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="label-eyebrow">Candidate</Label>
              <Select value={form.candidate_id || ""} onValueChange={(v) => {
                const c = candidates.find((x) => x.id === v);
                setForm({ ...form, candidate_id: v, candidate_name: c?.name, role_applied: c?.role_applied, branch: c?.branch });
              }} disabled={!!editing?.id}>
                <SelectTrigger className="mt-1.5" data-testid="interview-candidate-select">
                  <SelectValue placeholder="Pick a candidate" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {candidates.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.role_applied} · {c.branch}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Stage</Label>
              <Select value={form.stage || "L1"} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger className="mt-1.5" data-testid="interview-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="L1">L1</SelectItem><SelectItem value="L2">L2</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">When</Label>
              <Input type="datetime-local" value={form.scheduled_at || ""} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="mt-1.5" data-testid="interview-datetime-input" />
            </div>
            <div>
              <Label className="label-eyebrow">Duration (min)</Label>
              <Input type="number" min={15} step={15} value={form.duration_min || 30} onChange={(e) => setForm({ ...form, duration_min: Number(e.target.value) })} className="mt-1.5" />
            </div>
            <div>
              <Label className="label-eyebrow">Location</Label>
              <Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Zoom / Clinic name" className="mt-1.5" />
            </div>
            <div>
              <Label className="label-eyebrow">Interviewer</Label>
              <Input value={form.interviewer || ""} onChange={(e) => setForm({ ...form, interviewer: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="label-eyebrow">Interviewer email</Label>
              <Input type="email" value={form.interviewer_email || ""} onChange={(e) => setForm({ ...form, interviewer_email: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="label-eyebrow">Status</Label>
              <Select value={form.status || "Scheduled"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Scheduled", "Rescheduled", "Cleared", "Selected", "Rejected", "Cancelled", "Completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow">Notes</Label>
              <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
            </div>
          </div>
          <DialogFooter>
            {editing?.id && <Button variant="outline" onClick={remove} className="mr-auto text-[var(--yc-rose)] border-red-200 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 mr-1" /> Cancel interview</Button>}
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            <Button onClick={save} className="bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="save-interview-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
